import { championshipSwitcherScope, supportedBookReadStatus, worklistReadFailure } from '../features/command-center/championshipReadView.js'
/**
 * MyWorklist — the route that finally makes RepWorklist reachable.
 *
 * ⚠️ THIS FILE IS A CONTAINER AND NOTHING ELSE. The view, its four tiers, its
 * ranking and all five of its non-empty states live in
 * features/command-center/worklist/ and were built and asserted there. Slice 1
 * is ONLY the wiring: a URL, the real book, the real statuses. Measurement at
 * 390px and the per-rep screenshot proof are a later slice and are not here.
 *
 * ⚠️ NOT THE DEFAULT LANDING. `/` stays Home until the pilot with Zach Muller,
 * Philip Davanzo III and Faith Curtis says this screen is the one a rep should
 * open onto. Routing it is reversible; making it the front door is not.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api.js'
import { requestHunt } from '../features/command-center/pursuit/huntJob.js'
import { useAuth } from '../lib/auth.jsx'
import { useAccess } from '../lib/access.jsx'
import PursuitMoves from '../features/command-center/worklist/PursuitMoves.jsx'
import { useChampionships } from '../features/command-center/useChampionships.js'
import RepWorklist from '../features/command-center/worklist/RepWorklist.jsx'
import TradeOrders from '../features/trades/TradeOrders.jsx'
import BowlCommitteeWork from '../features/bowl/BowlCommitteeWork.jsx'
import BowlOrders from '../features/bowl/BowlOrders.jsx'
import SchoolTeamBook from '../features/bowl/SchoolTeamBook.jsx'
import PersonSwitcher, { useTeamScope } from '../features/command-center/worklist/PersonSwitcher.jsx'
/* The warning is imported rather than restated, so the sentence on the control
 * and the sentence in this form cannot drift apart. */
import { UNSOURCED_DATE_WARNING } from '../features/command-center/worklist/repWorklist.js'
import { inWorklistScope, worklistBookCounts, worklistPersonParams } from '../features/command-center/worklist/bookScope.js'
import { championshipPlaneIsComplete } from '../features/command-center/championshipPlaneEvidence.js'

/* ⚠️ THE TWO VOCABULARIES DO NOT MATCH, AND THE MISMATCH IS SILENT.
 * `useChampionships` reports loading | done | error; the worklist model's
 * `emptyKind()` reads loading | error | ready. Passing `done` through unmapped
 * falls into neither branch, so a loaded book would have been judged by the
 * ready path by accident rather than on purpose. Map it here, explicitly. */
const VIEW_STATUS = { loading: 'loading', done: 'ready', error: 'error' }

/* ⚠️ INLINE STYLES, DELIBERATELY, AND SCOPED TO THIS CONTAINER. The view owns
 * the design system — that is the standing division in this file and the hunt
 * status box above already follows it. Gathered into one object rather than
 * scattered through the JSX so the 44px rule and the wrap rules are visible in
 * one place and cannot be forgotten on the next control.
 *
 * MOBILE FIRST: every box is full width and wraps, every tap target is at least
 * 44px tall, nothing is `nowrap`, and nothing has a fixed width that could push
 * the page past 360px. Brian reads this on a phone. */
const SAY = {
  warn: {
    margin: '0 0 12px', padding: '10px 12px', borderRadius: 8, fontSize: 14, lineHeight: 1.5,
    border: '1px solid #b45309', background: '#fffbeb', color: '#7c2d12',
    overflowWrap: 'break-word', minWidth: 0,
  },
  form: {
    display: 'grid', gap: 10, minWidth: 0,
    margin: '0 0 12px', padding: '12px', borderRadius: 10, fontSize: 14,
    border: '1px solid #b08d57', background: 'rgba(176,141,87,0.07)',
  },
  formHead: { margin: 0, fontWeight: 700, lineHeight: 1.4, overflowWrap: 'break-word' },
  formReason: { margin: 0, lineHeight: 1.5, overflowWrap: 'break-word' },
  formWarn: {
    margin: 0, padding: '7px 9px', borderRadius: 8, lineHeight: 1.45,
    border: '1px solid #b45309', background: '#fffbeb', color: '#7c2d12',
    overflowWrap: 'break-word',
  },
  row: { display: 'flex', flexWrap: 'wrap', gap: 8, minWidth: 0 },
  label: { display: 'grid', gap: 4, minWidth: 0, fontSize: 13, fontWeight: 600 },
  input: {
    minHeight: 44, width: '100%', boxSizing: 'border-box', minWidth: 0,
    padding: '0 10px', borderRadius: 8, border: '1px solid rgba(128,128,128,0.45)',
    font: 'inherit', fontWeight: 400,
  },
  chip: (on) => ({
    minHeight: 44, padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
    font: 'inherit', fontWeight: 600, minWidth: 0,
    border: `1px solid ${on ? '#b08d57' : 'rgba(128,128,128,0.45)'}`,
    background: on ? '#b08d57' : 'transparent',
    color: on ? '#fff' : 'inherit',
  }),
  send: {
    minHeight: 44, padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
    font: 'inherit', fontWeight: 700, minWidth: 0,
    border: '1px solid #15803d', background: '#15803d', color: '#fff',
  },
  cancel: {
    minHeight: 44, padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
    font: 'inherit', minWidth: 0,
    border: '1px solid rgba(128,128,128,0.45)', background: 'transparent', color: 'inherit',
  },
}

export default function MyWorklist() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { hasRealRole, schoolTeamCoverage, refreshPermissions } = useAccess()
  useEffect(() => { refreshPermissions() }, [refreshPermissions])
  const championshipRead = useChampionships()
  const { data, status, error: sourceError, reload } = championshipRead
  const supportRead = supportedBookReadStatus(championshipRead)
  const countsComplete = championshipPlaneIsComplete(championshipRead) && supportRead.available

  /* ⚠️ AN IDENTITY WITH NO BOOK MUST SAY SO IN ITS OWN WORDS. The view has a
   * distinct `unmapped` voice precisely so that "we cannot tell what is yours"
   * never renders as the same screen as "you are clear". Deriving it from the
   * role is the only signal the frontend holds: the scope block the API returns
   * is not surfaced through AccessProvider, which exposes `bookMeta` and
   * `scopeError` but no scope mode. Slice 2 should thread the real
   * `scope.mode` through rather than inferring it here. */
  const scopeMode = hasRealRole ? 'own' : 'unmapped'

  /* Display-only selection within the championship rows the server returned.
   * The server remains authoritative for scope, pricing and every action. A
   * person query parameter is navigation state, never a grant of access. */
  const team = useTeamScope() // Existing action/trade scope; never broadened by read visibility.
  const championshipTeam = championshipSwitcherScope(championshipRead)
  const ownRep = user?.name || ''
  /* ⭐ THE DOOR FROM THE LAST HUNT (2026-10-26). The Command Center's last-hunt
   * sheet links each crown to `/my-worklist?person=<rep>&focus=<recordId>`, so a
   * crown opens on the worklist of the rep it belongs to, on its own row. The
   * person is only a starting view: the switcher still changes it, and the
   * server still decides what anyone may do. */
  const [params, setParams] = useSearchParams()
  const whose = params.get('person') || ''
  const error = worklistReadFailure({sourceError, supportError:supportRead.error, person:whose})
  const setWhose = (person) => setParams(worklistPersonParams(params, person))
  const focusIds = useMemo(() => (params.get('focus') || '').split(',').map((s) => s.trim()).filter(Boolean),
    [params])

  // Count exactly the same primary/support scope as the displayed worklist.
  // An incomplete or unhealthy read is unknown, never an authoritative zero.
  const repCounts = useMemo(() => worklistBookCounts(data || [], { complete: countsComplete, actor: user }), [data, countsComplete, user])

  const book = useMemo(() => {
    const all = data || []
    const want = whose || ownRep
    /* No identity to filter on, or the whole team asked for: show everything
     * the API returned rather than an empty list. An empty list here would be
     * the four-false-zeros fault — a confident nothing. */
    if (!want || want === '*') return all
    /* ⭐ THE PAIR'S SUPPORT SEAT WORKS THE LEAD'S BOOK. Brian: "Phil" means
     * Philip AND Madison; "Zach" means Zach AND Alexis. A record whose support
     * seat names this person — or whose lead is half of a standing pair — is on
     * their worklist only when authoritative support fields name them. `seatFor` keeps the owner match exactly as
     * it was for everybody else. */
    return all.filter((c) => inWorklistScope(c, want, {actor:want === ownRep ? user : null}))
  }, [data, whose, ownRep, user])

  const onOpen = useCallback((row) => {
    /* The Command Center already consumes `?record=` and opens the drawer on
     * arrival, waiting for the universe if the id lands first. Reusing that
     * door rather than building a second one is why this row is not inert. */
    if (row?.id && !row.record?.isIndyGroup) navigate(`/command-center?record=${encodeURIComponent(row.id)}`)
  }, [navigate])

  /* The hunt ask, and the only write on this screen. `requestHunt` already
   * exists and is the same call HuntBar, HuntNow and HuntWorkspace make, so
   * wiring it here is reuse rather than a second door. */
  const [hunting, setHunting] = useState('')
  const [huntSaid, setHuntSaid] = useState(null)

  /* ---- THE TRADE, AND THE CLAIM ---------------------------------------- *
   *
   * Brian: *"You haven't given the rep a proper way to trade an account
   * either."* The control has sat disabled on every card since it was written,
   * naming its own reason: a trade has to be ACCEPTED, and the record had no
   * pending state, so a button would have handed a record over whether or not
   * the other person agreed.
   *
   * ⭐ THAT PRECONDITION IS NOW MET. `/api/assignment-trade` carries a real
   * pending state with propose / accept / decline and a trail, so
   * `repWorklist.js` flips `disabled` off in THIS commit — and this handler
   * lands in the same one, because `MyWorklist.onAction` reports an unhandled
   * action as a red box at the top of the first screen a rep opens every
   * morning, and that has already happened once, to Brian, on production.
   *
   * ⚠️ ONE CALL FOR THE WHOLE SCREEN, NOT ONE PER ROW. Thirty eligibility
   * reads on mount would be thirty requests before a rep can scroll. The scope
   * read answers what is true of EVERY row — roster, window, what is already
   * pending — and the per-record eligibility is fetched on press, which is also
   * the only moment its answer can still be acted on. The server re-decides on
   * the write regardless: hard rule 5, the backend is what must refuse. */
  const [tradeScope, setTradeScope] = useState({ status: 'loading', data: null, error: '' })

  useEffect(() => {
    let live = true
    api.assignmentTrade()
      .then((d) => { if (live) setTradeScope({ status: 'done', data: d || null, error: '' }) })
      .catch((e) => {
        if (!live) return
        /* ⚠️ UNREACHABLE IS NOT "YOU MAY NOT TRADE". The controls stay disabled
         * saying they have not been told, and this sentence says why — a
         * refusal we invented here would be indistinguishable from the
         * server's. */
        setTradeScope({
          status: 'error',
          data: null,
          error: e?.body?.reason || e?.body?.detail || e?.message
            || 'The trade service could not be reached, so this screen does not know what may move.',
        })
      })
    return () => { live = false }
  }, [])

  /* ⚠️ THE RESPONSE CONTRACT NAMES `inbox`, `outbox` AND `window` WITHOUT
   * NAMING THE SHAPE OF AN ENTRY, so every key is read defensively and in one
   * place. Two consequences are deliberate:
   *
   *   · A MISSING `window` KEY READS AS OPEN, NOT LOCKED. Inferring a lock from
   *     an absent field would disable every control on the screen and blame the
   *     rep for it.
   *   · A PROPOSAL THAT CANNOT BE KEYED TO A RECORD IS COUNTED, NOT DROPPED.
   *     `unkeyed` is rendered below. A pending trade that silently fails to
   *     reach its card is the invisible-record fault in miniature. */
  const trade = useMemo(() => {
    const d = tradeScope.data || {}
    const w = d.window || null
    const windowLocked = w ? (w.open === false || w.locked === true) : false
    const windowWhy = (w && (w.why || w.reason)) || ''

    const base = {
      onRoster: d.onRoster === false ? false : (d.onRoster === true ? true : null),
      why: d.why || '',
      windowLocked,
      windowWhy,
      mayTrade: true,
      reason: '',
      pending: null,
    }

    const states = {}
    let unkeyed = 0
    const put = (p, direction) => {
      const id = String(p?.id ?? p?.recordId ?? p?.championshipId ?? '')
      const withRep = String(p?.fromRep ?? p?.toRep ?? p?.from ?? p?.to ?? '')
      if (!id) { unkeyed += 1; return }
      states[id] = { ...base, pending: { direction, withRep, reason: String(p?.reason || '') } }
    }
    for (const p of Array.isArray(d.inbox) ? d.inbox : []) put(p, 'in')
    for (const p of Array.isArray(d.outbox) ? d.outbox : []) put(p, 'out')

    return {
      /* `{}` while loading or failed: `actionsFor` reads a null tradeState as
       * "not told yet" and says exactly that, which is the third answer. */
      states: tradeScope.status === 'done' ? { '*': base, ...states } : {},
      unkeyed,
      rep: String(d.rep || ''),
      counts: d.counts || null,
    }
  }, [tradeScope])

  /* ⚠️ A REASON IS MANDATORY ON EVERY WRITE AND IS COLLECTED HERE, NOT
   * DISCOVERED AS A 400. The ledger has always required one, and the whole
   * reason `sr_assigned_rep` sat at 0% populated is that the Command Center
   * posted assignments without it for weeks. So the control arms a small form,
   * the form will not submit without a reason, and the server's own eligibility
   * sentence is fetched and shown INSIDE it — before the rep sends, which is
   * the only useful moment for the unsourced-date warning. */
  const [ask, setAsk] = useState(null)

  const armAsk = useCallback(async (kind, row, action) => {
    const pending = action?.pending || null
    setAsk({
      kind,
      id: row.id,
      name: row.name || '',
      pending,
      toRep: '',
      reason: '',
      decision: 'accept',
      busy: false,
      error: '',
      elig: null,
      eligError: '',
    })
    /* The server's sentence about THIS record, rendered verbatim. A failure
     * here does not block the form — it is reported and the write is still
     * re-decided server-side. */
    try {
      const e = await api.tradeEligibility(row.id)
      setAsk((a) => (a && a.id === row.id ? { ...a, elig: e?.eligibility || e || null } : a))
    } catch (err) {
      setAsk((a) => (a && a.id === row.id
        ? {
          ...a,
          eligError: err?.body?.reason || err?.message
            || 'The eligibility read failed, so this form cannot tell you in advance whether the '
            + 'date behind this championship is sourced. The write below is still checked by the server.',
        }
        : a))
    }
  }, [])

  const sendAsk = useCallback(async () => {
    if (!ask || ask.busy) return
    if (!ask.reason.trim()) {
      setAsk((a) => ({ ...a, error: 'A reason is required — the trade ledger refuses a write without one.' }))
      return
    }
    if (ask.kind === 'trade' && !ask.pending && !ask.toRep) {
      setAsk((a) => ({ ...a, error: 'Pick who you are offering this to.' }))
      return
    }
    setAsk((a) => ({ ...a, busy: true, error: '' }))
    try {
      let r
      if (ask.kind === 'claim') {
        r = await api.tradeClaim({ id: ask.id, reason: ask.reason.trim() })
      } else if (ask.pending) {
        r = await api.tradeDecide({ id: ask.id, decision: ask.decision, reason: ask.reason.trim() })
      } else {
        r = await api.tradePropose({ id: ask.id, toRep: ask.toRep, reason: ask.reason.trim() })
      }
      /* ⚠️ THE SERVER'S SENTENCE, NOT OURS. `says` is the human line these
       * routes return; `reason` is the eligibility sentence behind it. Writing
       * our own here is how two surfaces start disagreeing about what
       * happened. */
      setHuntSaid({
        id: ask.id,
        ok: true,
        text: r?.says || r?.eligibility?.reason
          || 'The trade service accepted that and did not say anything further.',
      })
      setAsk(null)
      /* A decision that MOVED the record changes the book. A proposal does not
       * move it — the rep still holds it — but the pending state is new, so
       * both reload. */
      reload()
    } catch (err) {
      setAsk((a) => ({
        ...a,
        busy: false,
        /* Every refusal on these routes returns a full sentence. Show it. */
        error: err?.body?.reason || err?.body?.detail || err?.message
          || 'The trade service refused that and did not say why, which is worth reporting rather than retrying.',
      }))
    }
  }, [ask, reload])

  const onAction = useCallback(async (action, row) => {
    // Summary items are not records. A deliberate selection must supply an actual member.
    if (row?.record?.isIndyGroup) return undefined
    if (action?.kind === 'open') return onOpen(row)
    /* Anchor actions (dial, compose) are real `<a href>` in the view and have
     * already done their work by the time this runs. */
    if (action?.href) return undefined

    /* ⚠️ A CONTROL THAT FALLS THROUGH THIS FUNCTION DOES NOTHING AND SAYS
     * NOTHING, WHICH IS THE WORST OF THE THREE OUTCOMES.
     *
     * Until 2.90.2 this handler returned silently for every kind except `open`,
     * while the view rendered a real enabled <button> for "Request a champion
     * hunt". Pressing it dropped the ask with no request and no error, so the
     * rep had no way to tell it apart from success. Unreachable, that was
     * harmless; linked for all nine reps in 2.89.0, it was a screen that lies.
     *
     * `hunt` is wired for real below. `exec` is disabled at the model with a
     * reason (see repWorklist.js) because it has no endpoint yet. Anything NEW
     * that reaches the end of this function is reported rather than swallowed —
     * an unhandled action should surface as an unhandled action. */
    /* 🔴 A DEAD BUTTON ON THE PRIMARY REP SURFACE, AND IT WAS MINE.
     *
     * `EMPTY.NO_BOOK` offers "Browse all championships" — the right thing to
     * give somebody whose book is still being allocated — and `onAction` had no
     * `browse` branch, so it fell through to my own "not wired up on this
     * screen yet" message. Brian pressed it on production and got a red box at
     * the top of the first screen a rep opens every morning.
     *
     * ⚠️ THE FALL-THROUGH MESSAGE IS WHY IT WAS VISIBLE RATHER THAN SILENT, and
     * that part worked — it is the guard I added in 2.90.2 after the exec-push
     * button swallowed presses. But "it reports itself" is not "it works". The
     * Command Center IS the full championship list, so this is a real
     * destination that already exists, not a new surface. */
    if (action?.kind === 'browse') { navigate('/command-center'); return undefined }

    // Every new commercial action reviews the exact record through the existing
    // guarded dossier/crown flow. Opening a dossier is not artwork or quote approval.
    if (action?.kind === 'art' || action?.kind === 'quote' || action?.kind === 'campaign' || action?.kind === 'win') {
      const act = action.kind === 'campaign' ? 'email' : action.kind
      if (row?.id) navigate(`/command-center?record=${encodeURIComponent(row.id)}&act=${act}`)
      return undefined
    }

    if (action?.kind === 'hunt') {
      if (action.disabled || !row?.id || hunting) return undefined
      setHunting(row.id)
      setHuntSaid(null)
      try {
        const r = await requestHunt(row.id)
        setHuntSaid({
          id: row.id,
          ok: true,
          /* Already-queued comes back as a SUCCESS with `alreadyQueued`, so say
           * which of the two happened rather than flattening both to "done". */
          text: r?.alreadyQueued
            ? 'Already in the hunt queue — your ask did not create a second one.'
            : (r?.message || 'Queued for the hand-hunt sweep.'),
        })
        reload()
      } catch (e) {
        setHuntSaid({ id: row.id, ok: false, text: e?.message || 'The hunt request did not go through. Nothing was queued.' })
      } finally {
        setHunting('')
      }
      return undefined
    }

    /* ⭐ THE TRADE AND THE CLAIM. Both arm the reason form rather than writing
     * on the first press: the server refuses a write with no reason, the
     * receiver has to be chosen, and the unsourced-date warning has to be read
     * BEFORE the record moves. A one-press trade would be the one-way throw
     * this control was disabled for in the first place. */
    if (action?.kind === 'trade' || action?.kind === 'claim') {
      if (action.disabled || !row?.id) return undefined
      setHuntSaid(null)
      await armAsk(action.kind, row, action)
      return undefined
    }

    setHuntSaid({ id: row?.id || '', ok: false,
      text: `"${action?.label || action?.kind}" is not wired up on this screen yet, so nothing was sent.` })
    return undefined
  }, [onOpen, hunting, reload, armAsk, navigate])

  const viewStatus = useMemo(() => VIEW_STATUS[status] || 'ready', [status])

  return (
    <>
      {/* ⚠️ THE ANSWER TO A WRITE IS RENDERED, NOT LOGGED. A hunt ask that
        * succeeds and a hunt ask that fails have to look different to the rep,
        * or the wiring above buys nothing over the silent no-op it replaced.
        * `role="status"` so a screen reader hears it; `aria-live` polite so it
        * does not interrupt. Styling is deliberately inline and minimal — this
        * is a container file and the view owns the design system. */}
      {huntSaid && (
        <div
          role="status"
          aria-live="polite"
          style={{
            margin: '0 0 12px', padding: '10px 12px', borderRadius: 8, fontSize: 14,
            border: `1px solid ${huntSaid.ok ? '#15803d' : '#b91c1c'}`,
            background: huntSaid.ok ? '#f0fdf4' : '#fef2f2',
            color: huntSaid.ok ? '#14532d' : '#7f1d1d',
          }}
        >
          {huntSaid.text}
        </div>
      )}

      {/* ⚠️ THE TRADE SERVICE'S OWN STATE, SAID OUT LOUD. Three answers that
        * look identical if they are not distinguished: it is still loading, it
        * could not be reached, or it does not place this identity on the
        * roster. Every one of them disables the controls, and a rep who is told
        * nothing concludes the buttons are broken. */}
      {tradeScope.status === 'error' && (
        <div role="status" aria-live="polite" style={SAY.warn}>
          <b>Trading and claiming are unavailable on this screen right now.</b>{' '}
          {tradeScope.error} Nothing is blocked by your permissions — this screen simply has no
          answer, and the controls say so on each card rather than looking broken.
        </div>
      )}
      {tradeScope.status === 'done' && tradeScope.data?.onRoster === false && (
        <div role="status" aria-live="polite" style={SAY.warn}>
          <b>The trade service does not place you on the roster.</b>{' '}
          {tradeScope.data?.why
            || 'It returned no sentence explaining that, which is worth reporting rather than retrying.'}
          {' '}That is a different answer from having no trades pending, so it is said here rather
          than rendered as a quiet empty inbox.
        </div>
      )}
      {/* 🔴 A PENDING TRADE THAT CANNOT REACH ITS CARD IS COUNTED, NEVER
        * DROPPED. If the service returns a proposal this screen cannot key to a
        * record, the row will not carry it — so the number is stated here. A
        * silent drop is the invisible-record fault at the scale of one rep's
        * morning. */}
      {trade.unkeyed > 0 && (
        <div role="status" aria-live="polite" style={SAY.warn}>
          <b>{trade.unkeyed} pending {trade.unkeyed === 1 ? 'trade' : 'trades'} could not be
            matched to a record on this screen</b>, so {trade.unkeyed === 1 ? 'it is' : 'they are'}{' '}
          not shown on any card below. {trade.unkeyed === 1 ? 'It has' : 'They have'} not gone
          away — ask for the trade trail rather than assuming nothing is waiting.
        </div>
      )}

      {/* ⭐ THE REASON FORM. A reason is mandatory on every write here and the
        * server answers 400 `reason_required` without one, so it is COLLECTED
        * rather than discovered. The eligibility sentence and the unsourced-date
        * warning are both inside the form, which is the only place a rep can
        * still act on them.
        *
        * ⚠️ INLINE STYLES ON PURPOSE. This file is a container and the view owns
        * the design system — the same decision the status box above already
        * made. Every tap target is 44px and every box wraps, because Brian reads
        * this on a phone. */}
      {ask && (
        <div style={SAY.form} role="group"
          aria-label={ask.kind === 'claim' ? 'Claim this record' : 'Trade this record'}>
          <p style={SAY.formHead}>
            {ask.kind === 'claim'
              ? 'Claim this record'
              : ask.pending
                ? `Accept or decline the trade from ${ask.pending.withRep || 'a teammate'}`
                : 'Offer this record to another rep'}
            {ask.name ? ` — ${ask.name}` : ''}
          </p>

          {/* The server's sentence about this record, verbatim. Never rewritten
            * here: the one on the response and the one on the form have to be
            * the same sentence or they will drift. */}
          {ask.elig?.reason && <p style={SAY.formReason}>{ask.elig.reason}</p>}
          {ask.eligError && <p style={SAY.formWarn}>{ask.eligError}</p>}

          {/* 🔴 THE UNSOURCED DATE, BEFORE THE SEND. Brian: a projected date
            * must not silently authorise or block a trade. The control already
            * carries this on the card; it is repeated here because this is the
            * press that moves the record. */}
          {ask.elig?.unsourced && <p style={SAY.formWarn}>{UNSOURCED_DATE_WARNING}</p>}

          {ask.kind === 'trade' && ask.pending && (
            <div style={SAY.row}>
              {[['accept', 'Accept it'], ['decline', 'Decline it']].map(([k, label]) => (
                <button key={k} type="button" style={SAY.chip(ask.decision === k)}
                  aria-pressed={ask.decision === k}
                  onClick={() => setAsk((a) => ({ ...a, decision: k }))}>{label}</button>
              ))}
            </div>
          )}

          {ask.kind === 'trade' && !ask.pending && (
            <label style={SAY.label}>
              Who are you offering it to?
              {/* ⚠️ THE OPTIONS COME FROM THE SERVER'S REP LIST, never a
                * hand-kept roster — a client list drifts the first time
                * somebody is hired and offers names the server will refuse
                * with 400 `receiver_off_roster`. */}
              <select style={SAY.input} value={ask.toRep}
                onChange={(e) => setAsk((a) => ({ ...a, toRep: e.target.value }))}>
                <option value="">Pick a teammate…</option>
                {(team.scope?.reps || []).filter((r) => r && r !== ownRep).map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              {!(team.scope?.reps || []).length && (
                <span style={SAY.formWarn}>
                  The server returned no teammate list, so there is nobody to offer this to from
                  here. That is a scope answer, not an empty team — ask for team visibility.
                </span>
              )}
            </label>
          )}

          <label style={SAY.label}>
            Why? (required)
            <input style={SAY.input} value={ask.reason}
              placeholder={ask.kind === 'claim'
                ? 'Why are you claiming this? (required)'
                : 'Why this trade? (required)'}
              onChange={(e) => setAsk((a) => ({ ...a, reason: e.target.value }))} />
          </label>

          {ask.error && <p style={SAY.formWarn} role="alert">{ask.error}</p>}

          <div style={SAY.row}>
            <button type="button" style={SAY.send} disabled={ask.busy} onClick={sendAsk}>
              {ask.busy
                ? 'Sending…'
                : ask.kind === 'claim'
                  ? 'Claim it'
                  : ask.pending ? 'Send the decision' : 'Send the offer'}
            </button>
            <button type="button" style={SAY.cancel} disabled={ask.busy} onClick={() => setAsk(null)}>
              Cancel — nothing is sent
            </button>
          </div>
        </div>
      )}
      {/* ⭐ THE PERSON-SWITCHER, IN THE WORK VIEW — not a settings screen. The
        * point is to change who you are looking at without leaving what you are
        * looking at. */}
      <PersonSwitcher
        scope={championshipTeam.scope}
        status={championshipTeam.status}
        error={championshipTeam.error}
        value={whose || ownRep}
        ownRep={ownRep}
        onChange={setWhose}
        counts={repCounts}
      />
      <p>School-team coverage returned for the signed-in person. This summary is separate from the person selected above.</p><SchoolTeamBook coverage={schoolTeamCoverage}/>
      {/* Every Pursuit board move on this book, with its next step (2026-10-26). */}
      <PursuitMoves book={book} onOpen={onOpen} />
      {/* ⭐ THE TRADES, AS WORK ORDERS (2026-10-19). Brian: "the trades should
        * be surfaced in the worklist". Until today this screen read only the
        * one-record proposal inbox, which is empty for anyone off the roster —
        * Brian included — and never saw a single desk offer. It follows the
        * person-switcher, so a manager looking at Dalton sees Dalton's trades. */}
      <TradeOrders who={whose || ownRep} />
      {/* ⭐ BOWL COMMITTEES (2026-10-26). John Haggerty's full-court press on
        * every bowl, plus the second person's share. Follows the switcher: own
        * items by default, anyone's for an exec (the server refuses a rep). */}
      <BowlCommitteeWork person={whose && whose !== ownRep ? whose : ''} />
      {/* ⭐ THE BOWL BOARD'S WORK (2026-10-26). Brian: "this all must be fully
        * integrated to worklist". Same work-order shape as the trades, same
        * person-switcher; each order opens the bowl row it came from. */}
      <BowlOrders who={whose || ownRep} />
      {whose === '*' && supportRead.error && !sourceError && <p role="status" style={SAY.warn}>{supportRead.error} The whole-team championship list remains visible; support-based counts are unavailable.</p>}
      <RepWorklist
        book={book}
        status={viewStatus}
        error={error || null}
        scopeMode={scopeMode}
        /* ⚠️ THE HEADER NAMES WHOSE LIST THIS IS. "What to act on, Zach" over
         * somebody else's records would be the worst kind of small lie on a
         * screen a manager acts from. `*` has no single name, so it falls back
         * to no name rather than inventing one. */
        repName={whose === '*' ? '' : (whose || ownRep)}
        /* ⚠️ THE TRADE SERVICE'S ANSWER, THREADED THROUGH THE VIEW TO THE
         * MODEL. `{}` while it is loading or unreachable, which `actionsFor`
         * reads as "not told yet" — a third answer, distinct from a refusal. */
        tradeStates={trade.states}
        /* The whole loaded object, so a split family is recognised even when
         * its parent and its rows sit in different books. Read-only. */
        universe={data || null}
        focusIds={focusIds}
        onOpen={onOpen}
        onAction={onAction}
        onRetry={reload}
      />
    </>
  )
}
